// Commissions as line items (Sept 2026): what a 3rd Party Cost card offers,
// and the total a rate per MT works out to.
//
// Mirrors the server, which owns both answers:
//   - category behaviour: three tick-boxes on ThirdPartyCostCategory, sent with
//     the category list (never read off the category's NAME -- a name can be
//     edited in the admin, and a renamed "Supply Desk" would start counting);
//   - the tonnage rule and the rounding: backend/fuel_enquiries/services/
//     per_mt_cost_service.py.
// The card shows what the save will write; after the save it shows what the
// server stored.
//
// BigInt is an ES2020 built-in the react-app lint environment (es6) does not
// list; without this line the dev server refuses to compile the file.
/* global BigInt */

import { formatAmount } from '../../utils/amountFormat';
import {
  AMOUNT_DECIMALS, PRICE_DECIMALS, QUANTITY_DECIMALS, multiplyRound,
} from '../../utils/precision';

export const categoryAllowsOnCharge = (category) => category?.allows_on_charge !== false;
export const categoryAllowsRatePerMt = (category) => category?.allows_rate_per_mt === true;
export const categoryCountsInTotals = (category) => category?.counts_in_totals !== false;

// A plain decimal string ("2.5", "1000.00") as a whole number (BigInt) of
// 1/10^scale units. Null for anything else, and for more decimals than
// ``scale`` -- the inputs feeding this are capped at the server's own scale, so
// a longer value means something upstream changed and a guess would be wrong.
const toScaledInteger = (value, scale) => {
  const match = /^(\d*)(?:\.(\d*))?$/.exec(String(value ?? '').trim());
  if (!match || (match[1] === '' && !match[2])) return null;
  const fraction = match[2] || '';
  if (fraction.length > scale) return null;
  return BigInt(`${match[1] || '0'}${fraction.padEnd(scale, '0')}`);
};

// A whole number of 1/10^scale units back to a plain "int.frac" string.
const scaledToDecimal = (units, scale) => {
  if (scale === 0) return units.toString();
  const digits = units.toString().padStart(scale + 1, '0');
  return `${digits.slice(0, -scale)}.${digits.slice(-scale)}`;
};

// The tonnages added up exactly, as a "1000.500000" string at the server's
// quantity scale -- the server sums before it multiplies, so the card does too.
// Null when one can't be read.
const summedTonnage = (tonnages) => {
  let units = 0n;
  for (const tonnage of tonnages) {
    const scaled = toScaledInteger(tonnage, QUANTITY_DECIMALS);
    if (scaled === null) return null;
    units += scaled;
  }
  return scaledToDecimal(units, QUANTITY_DECIMALS);
};

// rate (up to PRICE_DECIMALS) x summed tonnage (each up to QUANTITY_DECIMALS),
// rounded half-up to the cent, as a "2500.00" string -- the same figure the
// server stores (per_mt_total: Decimal product, quantize 0.01 ROUND_HALF_UP).
// Exact decimal arithmetic, never a float: 9.765625 x 1000.002048 is 9765.645
// exactly, which a float reads as 9765.64499... and puts on the wrong side of
// the half cent. Null when an input can't be read or carries more decimals
// than the server stores.
export const perMtTotal = (rate, tonnages) => {
  const rateUnits = toScaledInteger(rate, PRICE_DECIMALS);
  if (rateUnits === null) return null;
  const tonnage = summedTonnage(tonnages);
  if (tonnage === null) return null;
  return multiplyRound(scaledToDecimal(rateUnits, PRICE_DECIMALS), tonnage, AMOUNT_DECIMALS);
};

const noTonnageMessage = (subject, plural) => (
  `${subject} ${plural ? 'have' : 'has'} no MAX tonnage yet, so the total can't `
  + 'be worked out. Enter the MAX tonnage on the Product card and save the '
  + 'enquiry, or clear the rate and type the total instead.'
);

export const TICK_A_PRODUCT_MESSAGE = (
  'Tick the product this rate applies to. The rate is multiplied by the MAX '
  + 'tonnage of the product(s) you tick.'
);

const productLabel = (product, index) => (
  (product?.contract_shorthand || '').trim() || `Product ${index + 1}`
);

// What a rate on this card works out to, from the enquiry AS LAST SAVED: the
// server multiplies saved tonnage, so a MAX tonnage typed on the Product card
// but not saved yet must not show here either.
//
// Returns null when no rate is typed, otherwise one of
//   { total: '2500.00', working: '2.5 USD/mt × 1,000 mt (VLSFO ...)' }
//   { error: '<what to do>' }
//   { total: null, working: '' }   -- can't preview; the save works it out
export const perMtPreview = ({ enquiry, productIds, rate, currency }) => {
  const rateText = String(rate ?? '').trim();
  if (rateText === '') return null;

  const products = Array.isArray(enquiry?.products) ? enquiry.products : [];
  let tonnages;
  let basis;
  if (products.length === 0) {
    const tonnage = enquiry?.max_tonnage;
    if (tonnage == null || tonnage === '') {
      return { error: noTonnageMessage('This enquiry', false) };
    }
    tonnages = [tonnage];
    basis = '';
  } else {
    const wanted = new Set((productIds || []).map(Number));
    if (wanted.size === 0) return { error: TICK_A_PRODUCT_MESSAGE };
    const chosen = products
      .map((product, index) => ({ product, label: productLabel(product, index) }))
      .filter(({ product }) => wanted.has(Number(product.id)));
    if (chosen.length !== wanted.size) {
      return {
        error: 'A ticked product is no longer on this enquiry. Tick the '
          + 'product this rate applies to.',
      };
    }
    const missing = chosen
      .filter(({ product }) => product.max_tonnage == null || product.max_tonnage === '')
      .map(({ label }) => label);
    if (missing.length > 0) {
      return { error: noTonnageMessage(missing.join(', '), missing.length > 1) };
    }
    tonnages = chosen.map(({ product }) => product.max_tonnage);
    basis = chosen.map(({ label }) => label).join(' + ');
  }

  const total = perMtTotal(rateText, tonnages);
  // Unreadable: no preview, and no refusal either -- the server works the
  // figure out on save.
  if (total === null) return { total: null, working: '' };
  // The summed tonnage exactly as multiplied, not rounded to the cent.
  const tonnageText = summedTonnage(tonnages);
  const working = `${formatAmount(rateText)} ${currency || 'USD'}/mt × `
    + `${formatAmount(tonnageText)} mt${basis ? ` (${basis})` : ''}`;
  return { total, working };
};
